import { Controller } from "@hotwired/stimulus"
import { saveToCookie, loadFromCookie } from "../../helpers/cookie_storage"

const COLLAPSE_ALL_MARKER = "__all__"

/**
 * Controller for collapsible account sections in Dashboard V2.
 * Manages expand/collapse state and persists it via cookie.
 *
 * Cookie format:
 * - [] = all expanded (default)
 * - [123, 456] = only accounts 123 and 456 are collapsed
 * - ["__all__"] = all collapsed
 * - ["__all__", 123] = all collapsed EXCEPT account 123 (expanded as exception)
 */
export default class extends Controller {
  static targets = ["content", "icon"]
  static values = {
    accountId: Number,
    cookieName: String
  }

  connect() {
    // Read initial state from cookie
    this.collapsed = this.isCollapsed()
    this.updateUI()
  }

  isCollapsed() {
    const stored = loadFromCookie(this.cookieNameValue, [])
    const hasCollapseAll = stored.includes(COLLAPSE_ALL_MARKER)
    const hasAccountIdValue = stored.includes(this.accountIdValue)

    // In "collapse all" mode: collapsed UNLESS this ID is listed as an exception
    // In normal mode: expanded UNLESS this ID is listed as collapsed
    return hasCollapseAll ? !hasAccountIdValue : hasAccountIdValue
  }

  toggle(event) {
    // Don't toggle if clicking on a link - let the link navigate
    if (event.target.closest("a")) {
      return
    }

    event.preventDefault()
    this.collapsed = !this.collapsed
    this.persistState()
    this.updateUI()
  }

  persistState() {
    const stored = loadFromCookie(this.cookieNameValue, [])
    const hasCollapseAll = stored.includes(COLLAPSE_ALL_MARKER)

    if (hasCollapseAll) {
      // In "collapse all" mode: toggle this ID as an exception
      const set = new Set(stored)
      if (this.collapsed) {
        // Collapsing: remove from exceptions (no longer an exception)
        set.delete(this.accountIdValue)
      } else {
        // Expanding: add as an exception
        set.add(this.accountIdValue)
      }
      saveToCookie(this.cookieNameValue, [...set])
    } else {
      // Normal mode: track collapsed IDs
      const set = new Set(stored)
      if (this.collapsed) {
        set.add(this.accountIdValue)
      } else {
        set.delete(this.accountIdValue)
      }
      saveToCookie(this.cookieNameValue, [...set])
    }
  }

  // Called via event from expand all button
  expand() {
    this.collapsed = false
    this.updateUI()
  }

  // Called via event from collapse all button
  collapse() {
    this.collapsed = true
    this.updateUI()
  }

  // Expand all panels - clears everything, back to default
  expandAll(event) {
    event?.preventDefault()
    saveToCookie(this.cookieNameValue, [])
    document.querySelectorAll("[data-controller~='dashboard--collapsible']").forEach(el => {
      const controller = this.application.getControllerForElementAndIdentifier(el, "dashboard--collapsible")
      controller?.expand()
    })
  }

  // Collapse all panels - sets the collapse all marker
  collapseAll(event) {
    event?.preventDefault()
    saveToCookie(this.cookieNameValue, [COLLAPSE_ALL_MARKER])
    document.querySelectorAll("[data-controller~='dashboard--collapsible']").forEach(el => {
      const controller = this.application.getControllerForElementAndIdentifier(el, "dashboard--collapsible")
      controller?.collapse()
    })
  }

  updateUI() {
    if (this.hasContentTarget) {
      if (this.collapsed) {
        this.contentTarget.classList.add("hidden")
      } else {
        this.contentTarget.classList.remove("hidden")
      }
    }

    if (this.hasIconTarget) {
      // Rotate the chevron icon: points down when open, up when collapsed
      if (this.collapsed) {
        this.iconTarget.classList.add("rotate-180")
      } else {
        this.iconTarget.classList.remove("rotate-180")
      }
    }
  }
}
